/**
 * Login Page
 */

// Core imports
import React from 'react';
import PropTypes from 'prop-types';

// Style, SEO and settings
import Helmet from 'react-helmet';
import {
  Col,
  css,
  Grid,
  Row,
  up,
  styled,
  withTheme,
  thd,
} from '@smooth-ui/core-sc';

// Additional Components/Containers
import LoadAsync from '_platform/src/utils/LoadAsync';
import PublicComponent from '_platform/src/utils/PublicComponent';
import getReturnUrlPath from '_platform/src/utils/getReturnUrlPath';

import { withSettings } from 'containers/WebApp/SettingsContext';
import bgImage1x from './images/login.jpg';
import bgImage2x from './images/login@2x.jpg';
import logo from '../../theme/MaxRewardsLogoReversed@2x.png';

const LoginProvider = LoadAsync(() =>
  import(
    /* webpackChunkName: "loginProvider" */ '_platform/src/containers/LoginProvider/LoginProvider'
  )
);
const LoginForm = LoadAsync(() =>
  import(
    /* webpackChunkName: "loginForm" */ '_platform/src/components/LoginForm/LoginForm'
  )
);

const PageContainer = styled.div`
  align-items: center;
  background-color: #131d28;
  background-image: url(${bgImage1x});
  background-position: center top;
  background-repeat: no-repeat;
  background-size: cover;
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  justify-content: center;
  @media only screen and (min-device-pixel-ratio: 1.5),
    only screen and (min-resolution: 144dpi),
    only screen and (min-resolution: 1.5dppx) {
    background-color: #131d28;
    background-image: url(${bgImage2x});
  }
`;

const LoginLogo = styled.h1`
  margin-bottom: 0;
  margin-top: 0;
  padding-top: 0;
  line-height: 1;
  img {
    height: auto;
    width: 204px;
  }
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  background-color: rgba(0, 0, 0, 0.47);
  box-sizing: border-box;
  color: #fff;
  margin-bottom: 0.5rem;
  margin-top: 0.5rem;
  padding: 2rem 1.8rem;
  h2 {
    font-size: 30px;
    &.h2-intro {
      margin: 10px 0;
    }
  }
  p {
    color: #fff;
  }
  a {
    color: #fff;
    &:visited {
      color: #fff;
    }
  }
`;

const LoginContainer = styled.div`
  max-width: 500px;

  h2 {
    margin-top: 0;
  }

  p,
  label {
    font-size: 14px;
  }

  input.sui-input {
    padding: 0.5rem 0.75rem;
  }

  .sui-checkbox {
    .sui-checkbox-content {
      width: 22px;
      height: 22px;
      background-color: ${thd('primary', '#F68828')};
    }

    .sui-checkbox-check {
      color: black;
      width: 16px;
      height: 16px;
    }
  }

  button {
    color: #fff;
    font-size: 14px;
    font-weight: bold;
    padding: 0.75rem 0.75rem;
  }

  .reset-password {
    a {
      font-size: 14px;
      font-weight: normal;
    }
  }

  ${up(
    'lg',
    css`
      padding-left: 2rem;
      min-width: 300px;
    `
  )};
`;

const LoginPage = ({ settings }) => (
  <PublicComponent
    redirectOnError={
      // Redirect to returnUrl, if it's not the same as loginPagePath
      (getReturnUrlPath !== settings.settingsApp.loginPagePath &&
        getReturnUrlPath) ||
      '/'
    }
  >
    <Helmet>
      <title>Login</title>
      {/*<meta name="description" content="Page description" />*/}
    </Helmet>

    <PageContainer>
      <Grid className="page-container">
        <Container>
          <Grid>
            <Row justifyContent="center">
              <Col xs={12} sm={10} lg={7}>
                <LoginLogo className="text--left">
                  <img src={logo} alt="Max Rewards" />
                </LoginLogo>
                <h2 className="h2-intro text--primary">
                  Welcome to MAX Rewards
                </h2>
                <p className="spacer--bottom--small text--tight">
                  Max Rewards is Heatleys' exclusive loyalty program that gives
                  back to our valued customers.{' '}
                </p>

                <p className="spacer--bottom--small text--tight">
                  With every eligible purchase, you earn points that can be
                  redeemed for a wide range of rewards, from electronics and
                  appliances to entertainment and more.
                </p>

                <p className="spacer--bottom--small text--tight">
                  It's our way of saying thanks for choosing Heatleys.
                </p>
              </Col>
              <Col xs={12} sm={10} lg={5}>
                <LoginContainer>
                  <h2 className="text--primary">Login</h2>
                  <p>
                    Log in with the username and password you have been emailed.
                  </p>
                  <LoginProvider disableMeta>
                    <LoginForm formLabels={{ labelUserName: 'Username' }} />
                  </LoginProvider>
                </LoginContainer>
              </Col>
            </Row>
          </Grid>
        </Container>
      </Grid>
    </PageContainer>
  </PublicComponent>
);

LoginPage.propTypes = {
  settings: PropTypes.object.isRequired,
};

export default withTheme(withSettings(LoginPage));
